import { useEffect, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Grid } from "@vfuk/core-grid";
import Spacing from "@vfuk/core-spacing";
import manageAccessBanner from "@img/manageAccessBanner.png";
import RoleRow from "./RoleRow/RoleRow";
import {
  SectionBanner,
  TableControls,
  TableRowLoader,
  NoResultsRow,
  Crumbs,
  IconLink,
} from "@ucchub/ui";
import DashShell from "@components/common/DashShell";
import { useRoleManagementStore } from "src/hooks/stores";
import { useTranslation } from "react-i18next";
import SimpleGrid from "@vfuk/core-simple-grid/";
import UserPermission from "src/interfaces/userPermission";
import breadCrumbs from "@json/common/crumbs.json";
import ManageAccessLayout from "@components/layout/ManageAccessLayout";
import Container from "@vfuk/core-container";
import InfiniteScroll from "react-infinite-scroll-component";
import { RoleModel } from "@hub/common-libs/build/types/roles.service.model";

const Roles = () => {
  const { t } = useTranslation();
  const clearRolesData = useRoleManagementStore((state) => state.clearRoles);
  useEffect(() => {
    return () => {
      clearRolesData();
    };
  }, [clearRolesData]);
  const {
    fetched: rolesFetched,
    loading: rolesLoading,
    call: fetchRoles,
    error: roleError,
  } = useRoleManagementStore((state) => state.getRoles);
  const [roles, setRoles] = useState<RoleModel[]>([]);
  const paginationToken = useRoleManagementStore(
    (state) => state.rolePaginationKey
  );
  const navigate = useNavigate();

  const getRolesData = useCallback(async () => {
    const res = await fetchRoles(true);
    const joinRoles = roles.concat(JSON.parse(JSON.stringify(res)));
    setRoles(joinRoles);
  }, [fetchRoles, roles]);

  useEffect(() => {
    if (!rolesFetched) {
      getRolesData();
    }
  }, [getRolesData, rolesFetched]);

  const handleAddRoleClicked = () => {
    navigate("/roles/add");
  };
  const renderRolesRows = useCallback(() => {
    if (roles.length > 0) {
      return roles.map((role) => <RoleRow key={`row ${role}`} role={role} />);
    } else return null;
  }, [roles]);

  // Actions used for icons, labels and alt-text for action buttons
  const actions = useRoleManagementStore((state) => state.getRoles.roleActions);

  const userPermissions: UserPermission = useRoleManagementStore(
    (state) => state.getPermissions
  );

  const fetchMoreData = () => {
    if (paginationToken !== "") getRolesData();
  };

  return (
    <DashShell>
      <Crumbs items={breadCrumbs.roles} />
      <SectionBanner
        heading={t("shared:users:title")}
        subtitle={t("shared:users:subtitle") || "User details and roles"}
        iconName="users"
        image={manageAccessBanner}
      />
      <ManageAccessLayout activeTab="/roles" />
      <Spacing spacingLevel={{ bottom: 7 }}>
        <Container appearance="secondary">
          <Grid maxWidth="full">
            {actions?.add && rolesLoading !== true && (
              <TableControls
                id="users-controls"
                addButton={
                  actions.add ? (
                    <IconLink
                      appearance="primary"
                      text={actions?.add?.label}
                      icon={{
                        name: actions?.add?.icon,
                        justify: "left",
                      }}
                      onClick={handleAddRoleClicked}
                      size={2}
                      isDisabled={!userPermissions.canAdd}
                    />
                  ) : null
                }
              />
            )}
            <div role="table" aria-label="Portal roles">
              <InfiniteScroll
                dataLength={roles.length}
                next={fetchMoreData}
                hasMore={!!paginationToken}
                className="infiniteScroll-overflow"
                loader={
                  <TableRowLoader
                    checkbox
                    rowCount={1}
                    colCount={2}
                    colWidths={["50%", "50%"]}
                  />
                }
              >
                <SimpleGrid
                  columns={{
                    sm: 1,
                    md: 2,
                  }}
                  spacing={9}
                  verticalAlign="center"
                >
                  {renderRolesRows()}
                </SimpleGrid>
              </InfiniteScroll>
              {rolesLoading && paginationToken === "" && (
                <Spacing
                  spacingLevel={{
                    bottom: 3,
                    top: 12,
                  }}
                >
                  <TableRowLoader
                    checkbox
                    rowCount={1}
                    colCount={2}
                    colWidths={["50%", "50%"]}
                  />
                </Spacing>
              )}
              {roleError && (
                <NoResultsRow
                  text={
                    roleError !== undefined && roleError !== ""
                      ? roleError
                      : t("core:roles:no_roles")
                  }
                />
              )}
            </div>
          </Grid>
        </Container>
      </Spacing>
    </DashShell>
  );
};

export default Roles;
